import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface ChartDataItem {
    name: string;
    value: number;
    hours: number;
    [key: string]: string | number;
}

interface TimeDistributionChartProps {
    chartData: ChartDataItem[];
    colors: string[];
    selectedSkill: string | null;
    onSkillSelect: (skill: string | null) => void;
    loading: boolean;
    error: any;
}

const TimeDistributionChart = ({ chartData, colors, selectedSkill, onSkillSelect, loading, error }: TimeDistributionChartProps) => {
    const totalHours = chartData.reduce((sum, item) => sum + item.value, 0);

    // Find most productive skill (highest hours)
    const mostProductiveSkill = chartData.length > 0
        ? chartData.reduce((max, skill) => skill.value > max.value ? skill : max, chartData[0])
        : null;

    const handleSkillClick = (skillName: string) => {
        if (selectedSkill === skillName) {
            onSkillSelect(null); // Deselect if clicking the same skill
        } else {
            onSkillSelect(skillName);
        }
    };

    // Loading state - Skeleton loader
    if (loading) {
        return (
            <div className=" dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 animate-pulse"></div>
                        <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                    </div>

                    <div className="flex flex-row gap-8 items-center mq991:flex-col">
                        {/* Skeleton Pie Chart */}
                        <div className="w-[400px] h-[400px] mq991:w-full flex items-center justify-center">
                            <div className="relative">
                                <div className="w-56 h-56 rounded-full border-8 border-gray-200 dark:border-gray-700 animate-pulse"></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="flex gap-2">
                                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Skeleton Progress Bars */}
                        <div className="flex-1 flex flex-col gap-3">
                            {[1, 2, 3, 4].map((item) => (
                                <div key={item} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl animate-pulse">
                                    <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                    <div className="w-28 h-5 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                    <div className="flex-1 h-3 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                    <div className="w-20 h-5 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-800 shadow-lg">
                <div className="p-6">
                    <div className="flex items-center justify-center h-[400px]">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-red-600 dark:text-red-400 text-lg font-semibold mb-2">Error loading time distribution</p>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">{error.message}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Empty state - no data or all values are 0
    if (!chartData || chartData.length === 0 || totalHours === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="p-6">
                    <div className="flex items-center justify-center h-[400px]">
                        <div className="text-center">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                                <svg
                                    className="w-12 h-12 text-gray-400 dark:text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                    />
                                </svg>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 text-lg font-semibold mb-2">No data available</p>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No time tracked for the selected period</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 leftsidebar-card-dv overflow-hidden">
            <div className="p-6">
                <div className="flex justify-between items-center flex-wrap gap-4 mb-6 user-profile-main-blg">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
                            Time Distribution
                        </h3>
                    </div>
                </div>
                
                <div className="flex flex-row gap-8 items-center mq991:flex-col">
                    {/* Pie Chart with enhanced styling */}
                    <div className="w-[400px] h-[400px] mq991:w-full userprofile-chart-dv relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-2xl"></div>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={3}
                                    dataKey="value"
                                    onClick={(data) => handleSkillClick(data.name)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {chartData.map((entry, index) => {
                                        const isSelected = selectedSkill === entry.name;
                                        return (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={colors[index % colors.length]}
                                                stroke={isSelected ? '#2398ff' : '#fff'}
                                                strokeWidth={isSelected ? 5 : 2}
                                                style={{
                                                    filter: isSelected ? 'drop-shadow(0 6px 12px rgba(35, 152, 255, 0.5))' : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                                                    opacity: selectedSkill && !isSelected ? 0.4 : 1,
                                                    transition: 'all 0.3s ease',
                                                }}
                                            />
                                        );
                                    })}
                                </Pie>
                                <Tooltip
                                    formatter={(value: number) => `${value}h`}
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                        padding: '12px'
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {/* Center text showing total hours */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                                <div className="text-4xl font-bold text-gray-800 dark:text-white">{totalHours}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Total Hours</div>
                            </div>
                        </div>
                    </div>

                    {/* Skills List with enhanced Progress Bars */}
                    <div className="flex-1 flex flex-col gap-3">
                        {chartData.map((skill, index) => {
                            const percentage = totalHours > 0 ? ((skill.value / totalHours) * 100).toFixed(0) : '0';
                            const isSelected = selectedSkill === skill.name;
                            return (
                                <div
                                    key={index}
                                    onClick={() => handleSkillClick(skill.name)}
                                    className={`group relative overflow-hidden flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                                        isSelected
                                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-2 border-blue-400 dark:border-blue-500 shadow-lg scale-105 active-chart-progress-blg'
                                            : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600 chart-progress-blg'
                                    }`}
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    {isSelected && (
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-indigo-400/20 animate-pulse"></div>
                                    )}
                                    
                                    <div className="relative flex items-center gap-3 min-w-[140px]">
                                        <div className="relative">
                                            <div
                                                className={`w-4 h-4 rounded-full transition-all duration-300 ${
                                                    isSelected ? 'ring-4 ring-blue-300 dark:ring-blue-600 scale-125 zoom-in-zoom-out' : 'ring-2 ring-white dark:ring-gray-700'
                                                }`}
                                                style={{ backgroundColor: colors[index % colors.length] }}
                                            />
                                        </div>
                                        <span className={`text-sm font-medium transition-colors ${
                                            isSelected ? 'text-blue-700 dark:text-blue-300 font-bold' : 'text-gray-700 dark:text-gray-300'
                                        }`}>
                                            {skill.name}
                                        </span>
                                    </div>
                                    
                                    <div className="relative flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden shadow-inner">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ease-out relative ${
                                                isSelected ? 'shadow-lg' : ''
                                            }`}
                                            style={{
                                                width: `${percentage}%`,
                                                background: `linear-gradient(90deg, ${colors[index % colors.length]}, ${colors[index % colors.length]}dd)`,
                                                boxShadow: isSelected ? `0 0 12px ${colors[index % colors.length]}` : 'none'
                                            }}
                                        >
                                            {isSelected && (
                                                <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="relative flex items-baseline gap-2 min-w-[90px] justify-end">
                                        <span className={`text-xl font-bold transition-colors ${
                                            isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-800 dark:text-white'
                                        }`}>
                                            {skill.hours}h
                                        </span>
                                        <span className={`text-sm font-semibold transition-colors ${
                                            isSelected ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                                        }`}>
                                            {percentage}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Enhanced Total Time Tracked and Most Productive Section */}
                <div className="flex flex-row gap-6 mt-8 pt-6 border-t-2 border-gray-200 dark:border-gray-700 mq991:flex-col time-tracked-dv">
                    {/* Total Time Tracked */}
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl border border-blue-200 dark:border-blue-700 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md hour-btn-left">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Time Tracked</div>
                            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{totalHours} hours</h3>
                        </div>
                    </div>

                    {/* Most Productive */}
                    {mostProductiveSkill && (
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 shadow-sm hover:shadow-md transition-all duration-300 ml-auto mq991:ml-0">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div>
                                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Most Productive</div>
                                <div className="text-lg font-bold text-green-600 dark:text-green-400 mt-1">{mostProductiveSkill.name}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TimeDistributionChart;
export type { ChartDataItem };
